/**
 * Farlo UI | Marquee
 *
 * @author Farlo
 */

class FarloUiMarquee {
    constructor(_el) {
        this._el = _el;

        this.track = _el.querySelector(
            '.farlo-ui-marquee__track'
        );

        this.items = Array.from(
            _el.querySelectorAll(
                '.farlo-ui-marquee__item'
            )
        );

        this.position = 0;

        // Drag state
        this.isDragging = false;
        this.dragStartX = 0;
        this.dragStartPosition = 0;

        // Navigation
        this.prevBtn = _el.querySelector(
            '.farlo-ui-marquee__arrow--prev'
        );

        this.nextBtn = _el.querySelector(
            '.farlo-ui-marquee__arrow--next'
        );

        this._el.style.overflow = 'hidden';
        this._el.style.position = 'relative';

        this.duplicateItemsInfinite();
        this.refreshItems();

        this.originalWidth = this.track.scrollWidth;

        this.setResponsiveConfig();

        window.addEventListener('resize', () => {
            this.setResponsiveConfig();
        });

        this.initArrowEvents();
        this.initDragScroll();
        this.initWheelScroll();
    }

    refreshItems() {
        this.items = Array.from(
            this._el.querySelectorAll(
                '.farlo-ui-marquee__item'
            )
        );

        this.totalItems = this.items.length;
    }

    setResponsiveConfig() {
        const w = window.innerWidth;

        this.itemsPerScroll =
            w <= 640 ? 1 : w <= 980 ? 2 : 3;
    }

    duplicateItemsInfinite() {
        const original = this.track.innerHTML;

        this.track.innerHTML = original.repeat(5);

        this.track.style.display = 'flex';
        this.track.style.flexWrap = 'nowrap';
        this.track.style.willChange = 'transform';
    }

    /**
     * Get the total width of the marquee content.
     */
    getTotalWidth() {
        const gap =
            parseFloat(
                getComputedStyle(this.track).gap
            ) || 0;

        let totalWidth = 0;

        this.items.forEach((item, i) => {
            totalWidth += item.offsetWidth || 0;

            if (i < this.items.length - 1) {
                totalWidth += gap;
            }
        });

        return totalWidth;
    }

    /**
     * Keep the track position within the available range.
     */
    clampPosition(value) {
        const totalWidth = this.getTotalWidth();
        const viewportWidth = this._el.offsetWidth;
        const min = -(totalWidth - viewportWidth);

        if (value > 0) {
            return 0;
        }

        if (value < min) {
            return min;
        }

        return value;
    }

    /**
     * Navigation arrows.
     */
    initArrowEvents() {
        if (!this.prevBtn || !this.nextBtn) {
            return;
        }

        /**
         * Determine how far each arrow click should move.
         */
        const getStep = () => {
            const gap =
                parseFloat(
                    getComputedStyle(this.track).gap
                ) || 0;

            // Mobile: one item at a time.
            if (this.itemsPerScroll === 1) {
                const scrollOffset =
                    Math.abs(this.position);

                let runningWidth = 0;
                let currentIndex = 0;

                for (
                    let i = 0;
                    i < this.items.length;
                    i++
                ) {
                    runningWidth +=
                        (this.items[i].offsetWidth || 0) +
                        (i > 0 ? gap : 0);

                    if (
                        runningWidth > scrollOffset
                    ) {
                        currentIndex = i;
                        break;
                    }
                }

                const currentWidth =
                    this.items[currentIndex]
                        ?.offsetWidth || 0;

                return currentWidth + gap;
            }

            // Tablet/desktop: multiple items.
            let stepWidth = 0;

            for (
                let i = 0;
                i < this.itemsPerScroll;
                i++
            ) {
                const item = this.items[i];

                stepWidth +=
                    (item?.offsetWidth || 0) +
                    (i > 0 ? gap : 0);
            }

            return stepWidth;
        };

        /**
         * Update arrow states.
         */
        const updateArrows = () => {
            const totalWidth =
                this.getTotalWidth();

            const viewportWidth =
                this._el.offsetWidth;

            const scrollOffset =
                Math.abs(this.position);

            // Previous arrow.
            if (this.position >= 0) {
                this.prevBtn.style.opacity = '0.7';
                this.prevBtn.style.pointerEvents =
                    'none';
            } else {
                this.prevBtn.style.opacity = '1';
                this.prevBtn.style.pointerEvents =
                    'auto';
            }

            // Next arrow.
            if (
                scrollOffset >=
                totalWidth -
                    viewportWidth -
                    1
            ) {
                this.nextBtn.style.opacity = '0.7';
                this.nextBtn.style.pointerEvents =
                    'none';
            } else {
                this.nextBtn.style.opacity = '1';
                this.nextBtn.style.pointerEvents =
                    'auto';
            }
        };

        this.updateArrows = updateArrows;

        /**
         * Move the marquee manually.
         */
        const moveTrack = (delta) => {
            this.position =
                this.clampPosition(
                    this.position + delta
                );

            this.track.style.transition =
                'transform 0.4s ease';

            this.applyTransform();

            setTimeout(() => {
                this.track.style.transition = '';

                updateArrows();
            }, 400);
        };

        /**
         * Previous button.
         *
         * This is completely separate from dragging.
         */
        this.prevBtn.addEventListener(
            'click',
            (e) => {
                e.stopPropagation();

                moveTrack(getStep());
            }
        );

        /**
         * Next button.
         *
         * This is completely separate from dragging.
         */
        this.nextBtn.addEventListener(
            'click',
            (e) => {
                e.stopPropagation();

                moveTrack(-getStep());
            }
        );

        updateArrows();
    }

    /**
     * Drag-to-scroll with mouse and touch.
     */
    initDragScroll() {
        this._el.style.cursor = 'grab';
        this._el.style.touchAction = 'pan-y';

        /**
         * Check whether the pointer is over a navigation button.
         */
        const isNavigationButton = (target) => {
            return target.closest(
                '.farlo-ui-marquee__arrow--prev, ' +
                '.farlo-ui-marquee__arrow--next'
            );
        };

        /**
         * Start dragging.
         */
        const onPointerDown = (e) => {
            // IMPORTANT:
            // Navigation buttons are NOT draggable.
            if (isNavigationButton(e.target)) {
                return;
            }

            this.isDragging = true;

            this.dragStartX = e.clientX;

            this.dragStartPosition =
                this.position;

            this.track.style.transition = '';

            this._el.style.cursor = 'grabbing';

            this._el.setPointerCapture?.(
                e.pointerId
            );
        };

        /**
         * Move while dragging.
         */
        const onPointerMove = (e) => {
            if (!this.isDragging) {
                return;
            }

            const delta =
                e.clientX - this.dragStartX;

            this.position =
                this.clampPosition(
                    this.dragStartPosition +
                        delta
                );

            this.applyTransform();
        };

        /**
         * End dragging.
         */
        const endDrag = (e) => {
            if (!this.isDragging) {
                return;
            }

            this.isDragging = false;

            this._el.style.cursor = 'grab';

            if (
                e.pointerId !== undefined
            ) {
                try {
                    this._el.releasePointerCapture?.(
                        e.pointerId
                    );
                } catch (error) {
                    // Pointer capture may already
                    // have been released.
                }
            }

            if (this.updateArrows) {
                this.updateArrows();
            }
        };

        this._el.addEventListener(
            'pointerdown',
            onPointerDown
        );

        this._el.addEventListener(
            'pointermove',
            onPointerMove
        );

        this._el.addEventListener(
            'pointerup',
            endDrag
        );

        this._el.addEventListener(
            'pointercancel',
            endDrag
        );

        // Prevent native image/text dragging.
        this._el.addEventListener(
            'dragstart',
            (e) => {
                e.preventDefault();
            }
        );
    }

    /**
     * Horizontal scrolling with mouse wheel / trackpad.
     */
    initWheelScroll() {
        this._el.addEventListener(
            'wheel',
            (e) => {
                const delta =
                    Math.abs(e.deltaX) >
                    Math.abs(e.deltaY)
                        ? e.deltaX
                        : e.deltaY;

                if (delta === 0) {
                    return;
                }

                e.preventDefault();

                this.track.style.transition = '';

                this.position =
                    this.clampPosition(
                        this.position - delta
                    );

                this.applyTransform();

                if (this.updateArrows) {
                    this.updateArrows();
                }
            },
            {
                passive: false,
            }
        );
    }

    /**
     * Apply the current track position.
     */
    applyTransform() {
        this.track.style.transform =
            `translateX(${this.position}px)`;
    }
}

export default FarloUiMarquee;